import { z } from 'zod';

/**
 * GLOBAL SEARCH — one box that finds the thing, whatever kind of thing it is.
 *
 * A shop does not think in modules. Somebody rings up and says "Priya, the green lehenga" or
 * reads out "AB/2026-27/0042", and until now answering either meant knowing WHICH SCREEN that
 * lives on before you could start looking. That is a tax this product charged on every single
 * lookup, all day, to the person least able to afford it.
 *
 * Three decisions shape this.
 *
 * **Every result knows where it goes.** A hit carries the route that opens it, so finding and
 * doing are one gesture. A search that told you a customer exists and left you to navigate to
 * her is a search that saved nobody anything.
 *
 * **Sections are capped small and say their true total.** Five per kind, with the real count
 * beside it, because the box is a way to REACH one thing rather than a report — and "5 of 63
 * customers" tells somebody to type more, where a silent five tells them there are five.
 *
 * **Nothing is searched that the caller may not read.** The sections are assembled from the
 * caller's own permissions, so a karigar's box searches what a karigar's screens show and
 * nothing else. The result is not merely hidden; it is never looked for.
 */

/**
 * The kinds of thing one box can find.
 *
 * Deliberately the things a shop looks UP by name or number. Not everything in the product —
 * a search that returns audit entries and stock movements alongside a customer is one where
 * the answer somebody wanted is on the second screen.
 */
export const SEARCH_RESULT_TYPES = ['customer', 'order', 'invoice', 'product', 'staff'] as const;
export type SearchResultType = (typeof SEARCH_RESULT_TYPES)[number];
export const searchResultTypeSchema = z.enum(SEARCH_RESULT_TYPES);

export const searchResultSchema = z.object({
  type: searchResultTypeSchema,
  id: z.string(),
  /** What somebody was looking for: a name, an order number, a code. */
  title: z.string(),
  /** The second line that tells two Priyas apart — a phone number, a date, an amount. */
  subtitle: z.string().nullable(),
  /** Where it opens. Finding and doing are one gesture. */
  route: z.string(),
});
export type SearchResult = z.infer<typeof searchResultSchema>;

export const searchGroupSchema = z.object({
  type: searchResultTypeSchema,
  items: z.array(searchResultSchema),
  /** How many there really are, which is what tells somebody to type more. */
  total: z.number().int().nonnegative(),
});
export type SearchGroup = z.infer<typeof searchGroupSchema>;

export const globalSearchQuerySchema = z.object({
  q: z.string().trim().min(2, 'Type at least two characters').max(120),
});
export type GlobalSearchQuery = z.infer<typeof globalSearchQuerySchema>;

export const globalSearchResponseSchema = z.object({
  query: z.string(),
  groups: z.array(searchGroupSchema),
});
export type GlobalSearchResponse = z.infer<typeof globalSearchResponseSchema>;

/** How many hits one kind contributes before the box starts telling somebody to type more. */
export const SEARCH_GROUP_LIMIT = 5;
